import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

export default function MeetingSummary({
  data,
}: {
  data: Record<string, string[]> | null | undefined;
}) {
  const summaryKeys = useMemo(() => {
    const keys = Object.keys(data ?? {});
    const orderedKeys = ["Meeting Recap", "Next Steps"].filter((key) =>
      keys.includes(key)
    );
    const otherKeys = keys.filter((key) => !orderedKeys.includes(key));
    return [...orderedKeys, ...otherKeys];
  }, [data]);
  const [activeTab, setActiveTab] = useState(summaryKeys[0] ?? "");

  useEffect(() => {
    if (!summaryKeys.length) {
      setActiveTab("");
      return;
    }
    if (!activeTab || !summaryKeys.includes(activeTab)) {
      setActiveTab(summaryKeys[0]);
    }
  }, [activeTab, summaryKeys]);

  if (!Object.keys(data ?? {}).length) {
    return (
      <Stack justifyContent="center" mt={6} alignItems="center">
        <Typography color="text.secondary" variant="body2">
          Meeting summary not available for this recording
        </Typography>
      </Stack>
    );
  }

  const activeItems = activeTab ? data?.[activeTab] ?? [] : [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ToggleButtonGroup
          size="small"
          value={activeTab}
          exclusive
          fullWidth
          onChange={(_, value) => {
            if (value) setActiveTab(value);
          }}
          aria-label="Meeting summary sections"
          sx={{
            width: "100%",
            borderRadius: "10px",
            backgroundColor: "#f5f5f5",
            p: 0.5,
            "& .MuiToggleButton-root": {
              flex: 1,
              backgroundColor: "#f5f5f5",
              border: 0,
              textTransform: "none",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              borderRadius: 2,
            },
          }}
        >
          {summaryKeys.map((key) => (
            <ToggleButton key={key} value={key}>
              {key}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      <Grid item xs={12} sx={{ maxHeight: 350, overflow: "auto" }}>
        {activeItems.length ? (
          <Stack component="ul" pl={2} m={0} gap={1.5}>
            {activeItems.map((item, index) => (
              <Typography key={index} component="li" variant="body2">
                {item}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2">No summary details available.</Typography>
        )}
      </Grid>
    </Grid>
  );
}
